/**
 * Shared route metadata used for page titles and breadcrumb generation.
 *
 * @changeHistory
 * - 2026-07-07: Initial creation - Centralized route labels and breadcrumb descriptors (Codex)
 * - 2026-07-27: Renamed the profile route label to team member profile (Copilot)
 */

import { generatePath, matchPath } from 'react-router-dom';

export type BreadcrumbResolver =
  | 'agent'
  | 'listing-detail'
  | 'listing'
  | 'commercial-listing'
  | 'blog-post'
  | 'cda'
  | 'plan';

type StaticBreadcrumbConfig = {
  kind: 'static';
  label: string;
  href?: string;
};

type DynamicBreadcrumbConfig = {
  kind: 'dynamic';
  resolver: BreadcrumbResolver;
  param: string;
  fallbackLabel: string;
  hrefPattern?: string;
  fallbackFromParam?: boolean;
};

type BreadcrumbConfig = StaticBreadcrumbConfig | DynamicBreadcrumbConfig;

type RouteMetadata = {
  pattern: string;
  pageLabel: string;
  breadcrumbs: readonly BreadcrumbConfig[];
};

export type ResolvedBreadcrumb =
  | {
      kind: 'static';
      label: string;
      href?: string;
    }
  | {
      kind: 'dynamic';
      resolver: BreadcrumbResolver;
      value: string;
      fallbackLabel: string;
      href?: string;
    };

const ROUTE_METADATA: readonly RouteMetadata[] = [
  { pattern: '/login', pageLabel: 'Login', breadcrumbs: [{ kind: 'static', label: 'Login' }] },
  {
    pattern: '/status',
    pageLabel: 'System Status',
    breadcrumbs: [{ kind: 'static', label: 'System Status' }],
  },
  {
    pattern: '/dashboard',
    pageLabel: 'Dashboard',
    breadcrumbs: [{ kind: 'static', label: 'Dashboard' }],
  },
  {
    pattern: '/submit',
    pageLabel: 'Submit CDA',
    breadcrumbs: [{ kind: 'static', label: 'Submit CDA' }],
  },
  {
    pattern: '/cda/:id/edit',
    pageLabel: 'Edit CDA',
    breadcrumbs: [
      {
        kind: 'dynamic',
        resolver: 'cda',
        param: 'id',
        fallbackLabel: 'Edit CDA',
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  { pattern: '/team', pageLabel: 'Team', breadcrumbs: [{ kind: 'static', label: 'Team' }] },
  {
    pattern: '/agents/:agentId/edit',
    pageLabel: 'Edit Agent',
    breadcrumbs: [
      { kind: 'static', label: 'Agents', href: '/agents' },
      {
        kind: 'dynamic',
        resolver: 'agent',
        param: 'agentId',
        fallbackLabel: 'Agent Detail',
        hrefPattern: '/agents/:agentId',
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  {
    pattern: '/agents/:agentId',
    pageLabel: 'Agent Detail',
    breadcrumbs: [
      { kind: 'static', label: 'Agents', href: '/agents' },
      {
        kind: 'dynamic',
        resolver: 'agent',
        param: 'agentId',
        fallbackLabel: 'Agent Detail',
      },
    ],
  },
  {
    pattern: '/agents',
    pageLabel: 'Agents',
    breadcrumbs: [{ kind: 'static', label: 'Agents' }],
  },
  {
    pattern: '/compensation-plans/new',
    pageLabel: 'New Compensation Plan',
    breadcrumbs: [
      { kind: 'static', label: 'Comp Plans', href: '/compensation-plans' },
      { kind: 'static', label: 'New' },
    ],
  },
  {
    pattern: '/compensation-plans/:id/edit',
    pageLabel: 'Edit Compensation Plan',
    breadcrumbs: [
      { kind: 'static', label: 'Comp Plans', href: '/compensation-plans' },
      {
        kind: 'dynamic',
        resolver: 'plan',
        param: 'id',
        fallbackLabel: 'Compensation Plan',
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  {
    pattern: '/compensation-plans',
    pageLabel: 'Compensation Plans',
    breadcrumbs: [{ kind: 'static', label: 'Comp Plans' }],
  },
  {
    pattern: '/plans',
    pageLabel: 'Compensation Plans',
    breadcrumbs: [{ kind: 'static', label: 'Comp Plans' }],
  },
  {
    pattern: '/transactions',
    pageLabel: 'Transactions',
    breadcrumbs: [{ kind: 'static', label: 'Transactions' }],
  },
  {
    pattern: '/listings/new',
    pageLabel: 'New Listing',
    breadcrumbs: [
      { kind: 'static', label: 'Listings', href: '/listings' },
      { kind: 'static', label: 'New' },
    ],
  },
  {
    pattern: '/listings/commercial/:id/edit',
    pageLabel: 'Edit Listing',
    breadcrumbs: [
      { kind: 'static', label: 'Listings', href: '/listings' },
      {
        kind: 'dynamic',
        resolver: 'commercial-listing',
        param: 'id',
        fallbackLabel: 'Listing Details',
        hrefPattern: '/listings/:id',
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  {
    pattern: '/listings/:id/edit',
    pageLabel: 'Edit Listing',
    breadcrumbs: [
      { kind: 'static', label: 'Listings', href: '/listings' },
      {
        kind: 'dynamic',
        resolver: 'listing',
        param: 'id',
        fallbackLabel: 'Listing Details',
        hrefPattern: '/listings/:id',
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  {
    pattern: '/listings/:id',
    pageLabel: 'Listing Details',
    breadcrumbs: [
      { kind: 'static', label: 'Listings', href: '/listings' },
      {
        kind: 'dynamic',
        resolver: 'listing-detail',
        param: 'id',
        fallbackLabel: 'Listing Details',
      },
    ],
  },
  {
    pattern: '/listings',
    pageLabel: 'Listings',
    breadcrumbs: [{ kind: 'static', label: 'Listings' }],
  },
  {
    pattern: '/blog-posts/new',
    pageLabel: 'New Blog Post',
    breadcrumbs: [
      { kind: 'static', label: 'Blog', href: '/blog-posts' },
      { kind: 'static', label: 'New' },
    ],
  },
  {
    pattern: '/blog-posts/:slug/edit',
    pageLabel: 'Edit Blog Post',
    breadcrumbs: [
      { kind: 'static', label: 'Blog', href: '/blog-posts' },
      {
        kind: 'dynamic',
        resolver: 'blog-post',
        param: 'slug',
        fallbackLabel: 'Blog Post',
        fallbackFromParam: true,
      },
      { kind: 'static', label: 'Edit' },
    ],
  },
  {
    pattern: '/blog-posts',
    pageLabel: 'Blog Management',
    breadcrumbs: [{ kind: 'static', label: 'Blog' }],
  },
  {
    pattern: '/reporting',
    pageLabel: 'Reporting',
    breadcrumbs: [{ kind: 'static', label: 'Reporting' }],
  },
  {
    pattern: '/audit-logs',
    pageLabel: 'Audit Logs',
    breadcrumbs: [{ kind: 'static', label: 'Audit Logs' }],
  },
  {
    pattern: '/admin/diagnostics',
    pageLabel: 'Diagnostics',
    breadcrumbs: [
      { kind: 'static', label: 'Admin', href: '/admin' },
      { kind: 'static', label: 'Diagnostics' },
    ],
  },
  {
    pattern: '/admin',
    pageLabel: 'Admin Dashboard',
    breadcrumbs: [{ kind: 'static', label: 'Admin' }],
  },
  {
    pattern: '/profile',
    pageLabel: 'Team Member Profile',
    breadcrumbs: [{ kind: 'static', label: 'Team Member Profile' }],
  },
  {
    pattern: '/title-companies/import',
    pageLabel: 'Import Title Companies',
    breadcrumbs: [
      { kind: 'static', label: 'Title Companies', href: '/title-companies' },
      { kind: 'static', label: 'Import' },
    ],
  },
  {
    pattern: '/title-companies',
    pageLabel: 'Title Companies',
    breadcrumbs: [{ kind: 'static', label: 'Title Companies' }],
  },
  { pattern: '/', pageLabel: 'Home', breadcrumbs: [{ kind: 'static', label: 'Dashboard' }] },
] as const;

const humanizeText = (value: string): string =>
  value
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const isOpaqueIdentifier = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value) ||
  /^\d+$/.test(value);

const getFallbackLabel = (config: DynamicBreadcrumbConfig, value: string): string => {
  if (config.fallbackFromParam && value.trim().length > 0) {
    return humanizeText(value);
  }

  return config.fallbackLabel;
};

const buildFallbackBreadcrumbs = (pathname: string): ResolvedBreadcrumb[] => {
  const segments = pathname.replace(/^\/+/, '').split('/').filter(Boolean);

  if (segments.length === 0) {
    return [{ kind: 'static', label: 'Dashboard' }];
  }

  let href = '';

  return segments.map((segment, index) => {
    href += `/${segment}`;
    const isLast = index === segments.length - 1;
    let label = humanizeText(segment);

    if (index > 0 && isOpaqueIdentifier(segment)) {
      label = 'Detail';
    }

    return {
      kind: 'static' as const,
      label,
      href: isLast ? undefined : href,
    };
  });
};

export const matchRouteMetadata = (
  pathname: string
): { route: RouteMetadata; params: Record<string, string> } | null =>
  ROUTE_METADATA.reduce<{ route: RouteMetadata; params: Record<string, string> } | null>(
    (matchedRoute, route) => {
      if (matchedRoute) {
        return matchedRoute;
      }

      const match = matchPath({ path: route.pattern, end: true }, pathname);
      if (!match) {
        return null;
      }

      const params = Object.fromEntries(
        Object.entries(match.params).filter((entry): entry is [string, string] => !!entry[1])
      );

      return { route, params };
    },
    null
  );

export const getPageLabel = (pathname: string): string =>
  matchRouteMetadata(pathname)?.route.pageLabel ?? 'Page';

export const getBreadcrumbDescriptors = (pathname: string): ResolvedBreadcrumb[] => {
  const matchedRoute = matchRouteMetadata(pathname);
  if (!matchedRoute) {
    return buildFallbackBreadcrumbs(pathname);
  }

  const { route, params } = matchedRoute;

  return route.breadcrumbs.map((crumb) => {
    if (crumb.kind === 'static') {
      return {
        kind: 'static' as const,
        label: crumb.label,
        href: crumb.href,
      };
    }

    const value = params[crumb.param] ?? '';

    return {
      kind: 'dynamic' as const,
      resolver: crumb.resolver,
      value,
      fallbackLabel: getFallbackLabel(crumb, value),
      href:
        crumb.hrefPattern && value
          ? generatePath(crumb.hrefPattern, { [crumb.param]: value })
          : undefined,
    };
  });
};
